"use client";

import * as React from "react";
import { File as FileIcon, X } from "lucide-react";
import type { FileError } from "react-dropzone";
import { formatBytes as formatFileSize } from "@jasperlabs/number-format";

import { withOccurrenceKeys } from "../../lib/occurrence-keys";
import { cn } from "../../lib/utils";
import { FieldError } from "./field";

export type ExistingFile = {
	id: string;
	name: string;
	size: number;
	url?: string;
};

export type FileCardProps = {
	file: File | ExistingFile;
	onRemove?: (file: File | ExistingFile) => void;
	disabled?: boolean;
	isInvalid?: boolean;
	errors?: readonly FileError[];
	maxSizeInBytes?: number;
	/** File extensions or MIME types listed in the "invalid type" error, e.g. `[".pdf", ".png"]`. */
	acceptedTypes?: readonly string[];
	hideFileSize?: boolean;
	/**
	 * Controls rendered before the remove button, e.g. a download link for a stored file.
	 * Unlike remove, these stay visible while `disabled`, so a read-only card can still be acted on.
	 */
	actions?: React.ReactNode;
	className?: string;
	"data-testid"?: string;
	removeButtonTestId?: string;
};

// Binary (1024) formatter, used only for size *limits*, which are defined in
// mebibytes (e.g. `3 * 1024 * 1024`) so they render as clean values ("3 MB").
// Actual file sizes are displayed with the shared decimal (1000) `formatFileSize`
// so they match the rest of the app (e.g. 2,000,000 bytes -> "2 MB").
function formatBytes(bytes: number, decimals = 1): string {
	if (bytes === 0) {
		return "0 B";
	}
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
	const i = Math.min(
		Math.floor(Math.log(bytes) / Math.log(k)),
		sizes.length - 1,
	);
	return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

export function getErrorMessage(
	error: FileError,
	maxSizeInBytes?: number,
	acceptedTypes?: readonly string[],
): string {
	switch (error.code) {
		case "file-too-large":
			return maxSizeInBytes
				? `File is too large. Maximum size is ${formatBytes(maxSizeInBytes)}.`
				: "File is too large.";
		case "file-too-small":
			return "File is too small.";
		case "file-invalid-type":
			return acceptedTypes?.length
				? `File is an invalid type. Accepted types are ${acceptedTypes.join(", ")}.`
				: "File type is not accepted. Select a file type accepted by this upload field.";
		case "too-many-files":
			return "Too many files.";
		default:
			return error.message;
	}
}

const FileCard = React.forwardRef<HTMLDivElement, FileCardProps>(
	(
		{
			file,
			onRemove,
			disabled,
			isInvalid,
			errors = [],
			maxSizeInBytes,
			acceptedTypes,
			hideFileSize,
			actions,
			className,
			"data-testid": testId,
			removeButtonTestId,
		},
		ref,
	) => (
		<div
			ref={ref}
			data-slot="file-card"
			className={cn("w-full text-left", className)}
			data-testid={testId}
		>
			<div
				className={cn(
					"flex items-center justify-between rounded-lg border border-neutral-200 bg-background p-3",
					isInvalid && "border-danger-300 bg-danger-50",
				)}
			>
				<div className="flex min-w-0 flex-1 items-center gap-3">
					<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-blue-100">
						<FileIcon className="h-5 w-5 text-blue-500" />
					</div>
					<div className="min-w-0 flex-1">
						<p className="truncate text-sm font-medium text-neutral-900">
							{file.name}
						</p>
						{!hideFileSize && (
							<p className="truncate text-xs text-neutral-500">
								{formatFileSize(file.size)}
							</p>
						)}
					</div>
				</div>

				{(actions || (!disabled && onRemove)) && (
					<div className="ml-3 flex shrink-0 items-center gap-1">
						{actions}

						{!disabled && onRemove && (
							<button
								type="button"
								onClick={(e) => {
									e.preventDefault();
									onRemove(file);
								}}
								className="shrink-0 p-1 text-neutral-400 transition-colors hover:text-neutral-600"
								aria-label={`Remove ${file.name}`}
								data-testid={removeButtonTestId}
							>
								<X className="h-4 w-4" />
							</button>
						)}
					</div>
				)}
			</div>

			{errors.length > 0 && (
				<FieldError className="mt-1.5">
					{errors.length === 1 ? (
						getErrorMessage(errors[0], maxSizeInBytes, acceptedTypes)
					) : (
						<ul className="list-disc pl-4">
							{withOccurrenceKeys(errors, (error) => error.code).map(
								({ item: error, key }) => (
									<li key={key}>
										{getErrorMessage(error, maxSizeInBytes, acceptedTypes)}
									</li>
								),
							)}
						</ul>
					)}
				</FieldError>
			)}
		</div>
	),
);
FileCard.displayName = "FileCard";

export { FileCard, formatBytes };
